import { RootState } from '../store/store';
import { PaymentPeriod, PeriodLicense } from 'src/types/tax';
import { License } from 'src/types/licenses';
import { getAPIManagerInstance } from './apiManager';
import {
  DECKARD_API_URL,
  httpMethodFunctions,
  PaginatedResponse,
} from './common';
import { AxiosResponse } from 'axios';
import { selectCurrentIDToken } from 'src/store/slices/auth/authSelectors';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import { sanitizePeople } from './utils';
import { RENTAL_STRUCTURE_STATUS_ENUM } from 'src/common/enums';
import { RequiredHiddable } from 'src/types/configuration';

const ERR_LICENSES = 'Something went wrong while fetching licenses.';

export const createNewLicense = async (
  data: ApplicationData,
  accessToken: string,
  rentalStructureRequiredStatus: RequiredHiddable
): Promise<{ applicationNumber: string }> => {
  const {
    registrant,
    property,
    contacts,
    propertyDetails,
    listings,
    certifications,
  } = data;
  const apn = property?.propertyid;
  let registrantType = registrant?.registrantType ?? '';
  if (registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT) {
    registrantType = REGISTRANT_OPTIONS.PROPERTY_MANAGER;
  }
  const applicationConfig = {
    registrantType,
    unitNumber: property?.unitnumber ?? '',
    // only add rentalStructure property if it is not set to hidden in the config
    ...(rentalStructureRequiredStatus !==
      RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
      rentalStructure: property?.rentalstructure ?? null,
    }),
    misc: {
      supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
      mandatoryAcknowledgements: {
        ...certifications?.mandatoryAcknowledgements,
      },
    },
    people: {
      ...sanitizePeople(registrant),
      ...sanitizePeople(contacts),
    },
    signatory: certifications?.signatory ?? '',
    licenseListings: [] as Array<{ url: string }>,
  };

  const parsedListings: Array<{ url: string }> =
    listings?.map((l: string) => {
      return { url: l };
    }) || [];
  if (parsedListings.length > 0) {
    applicationConfig.licenseListings = parsedListings;
  }

  const response = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/newLicense/${apn}`,
    applicationConfig,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to create apply a license');
};

export const getTaxPaymentPeriodLicenses = async (
  state: RootState,
  period: { startDate: string },
  contToken: string,
  cache: string
) => {
  const accessToken = selectCurrentIDToken(state);

  const response = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/totPaymentPeriodLicenses`,
    period,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        contToken,
        cache,
      },
    }
  );
  if (response.status === 200) {
    return {
      contToken: response.data.contToken,
      data: response.data.licenses,
    };
  } else {
    throw new Error('Failed to fetch renewable licenses');
  }
};

export const getTaxableLicenses = async (
  dates: Array<string>,
  state: RootState
): Promise<Record<string, Array<PeriodLicense>>> => {
  // Get a stable string to user as our cache key
  const now = new Date().getTime().toString();

  // Paginate through all of the licenses for a given payment period date.
  // Collect the results into an array for further use.
  const go = async (
    startDate: string,
    contToken: string
  ): Promise<Array<PeriodLicense>> => {
    const result = await getTaxPaymentPeriodLicenses(
      state,
      { startDate },
      contToken,
      now
    );
    const newContToken: string | null = result.contToken;
    const licenses: Array<PeriodLicense> = result.data;
    // No cont token means we are done.
    if (!newContToken) {
      return licenses;
    } else {
      const nextPageLicenses = await go(startDate, newContToken);
      return [...licenses, ...nextPageLicenses];
    }
  };

  // For each period date that we have been given, collect all of the licenses matching
  // that payment period, returning them in a date-keyed object.
  return dates.reduce(async (prev, date) => {
    return {
      ...prev,
      [date]: await go(date, '0'),
    };
  }, {});
};

export const getTaxablePeriods = async (
  state: RootState
): Promise<Array<PaymentPeriod>> => {
  const accessToken = selectCurrentIDToken(state);
  const result = await httpMethodFunctions.get<Array<PaymentPeriod>>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/totPaymentPeriodDates`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return result.data;
};

export const getUserRenewableLicenses = async (
  accessToken: string,
  contToken?: number
): Promise<PaginatedResponse<License>> => {
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/renewableLicenses`;
  const response = await httpMethodFunctions.get<{
    contToken: number;
    licenses: License[];
  }>(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    ...((contToken || contToken === 0) && {
      params: {
        contToken,
      },
    }),
  });

  if (response.status === 200) {
    return {
      contToken: response.data.contToken,
      data: response.data.licenses,
    };
  } else {
    throw new Error('Failed to fetch renewable licenses');
  }
};

/* This is kept for backwards compatibility, but we should remove any reference to this function
 * in the future.
 */
export const getUserLicensesAsync = async (
  accessToken: string,
  contToken?: number
): Promise<AxiosResponse<{ contToken: number; licenses: License[] }>> => {
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/userLicenses`;

  return httpMethodFunctions.get<{ contToken: number; licenses: License[] }>(
    url,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      ...((contToken || contToken === 0) && {
        params: {
          contToken,
        },
      }),
    }
  );
};

export const getUserLicenses = async (
  accessToken: string,
  contToken?: number
): Promise<PaginatedResponse<License>> => {
  const response = await getUserLicensesAsync(accessToken, contToken);
  if (response.status === 200) {
    return {
      contToken: response.data.contToken,
      data: response.data.licenses,
    };
  } else {
    throw new Error('Failed to fetch user licenses');
  }
};

export const getAllLicensesForUserAsync = async (accessToken: string) => {
  const accumulatedLicenses: License[] = [];

  const fetchLicenses = async (contToken: number) => {
    try {
      const response = await getUserLicensesAsync(accessToken, contToken);
      const { data } = response;
      const lics = [...accumulatedLicenses, ...data.licenses];

      if (data.contToken) {
        // Recursively call the function if contToken exists
        return fetchLicenses(data.contToken);
      } else {
        // Return accumulated licenses
        return lics;
      }
    } catch (error) {
      throw new Error(ERR_LICENSES);
    }
  };

  return fetchLicenses(0);
};
